import React, { useEffect, useMemo, useState } from "react";
import {
    Select,
    Button,
    Row,
    Typography,
    message,
    Card,
    Space,
    Divider,
    Segmented,
    Tooltip
} from "antd";

import { useDispatch, useSelector } from "react-redux";
import { audioMessage } from "./Notifications/audioMessage";
import { SelectWithAdd } from "./SelectWithAdd";
import { useAuth0 } from "@auth0/auth0-react";
import { trainingAPI } from "../../api/TrainingAPI";
import { setTeamPresets } from "../../store/actions/team.actions";
import { IGeneralReportsConfig, IReportTypes } from "../Report/constants";
import { getChartType } from "../../Utils/chart_utils";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization";
import { metricsSelectDropdown } from "./utils";


const { Option } = Select;
const { Title, Text } = Typography;

const getEmptySelections = (count: number) => {
    let res = [];
    for (let i = 0; i < count; i++) {
        res.push({ 'metric': undefined, 'agg': 'avg' });
    }
    return res;
}

export const MultiMetricPicker = ({ requirements, setResult, setOpen, showAggs = false, noAsym = true, presetInfo, config, metricSelectorPrefix }: {
    showAggs: boolean,
    noAsym: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setResult: (x: any) => void,
    requirements?: IReportTypes,
    presetInfo?: {
        items: { name: string, config: any[], type: string, creator_id__email: string, preset_id: number }[]
    },
    config?: IGeneralReportsConfig,
    metricSelectorPrefix?: string
}) => {
    const dispatch = useDispatch();
    const {
        positions,
        dropdowns: { forceplate: forceplateDropdowns,
            testing: testingDropdowns,
            gps: gpsDropdowns,
        },
        playerGroups: groups,
        allPlayers,
        all_metric_types,
        info: { team_colors: teamColors },
    } = useSelector(({ team }) => team);

    const { user } = useAuth0();
    useMetricDropdowns(['forceplate', 'testing', 'gps']);
    showAggs = showAggs || requirements?.metric_selector_info?.useAggs || false;

    const [unsavedPresetChanges, setUnsavedPresetChanges] = useState<boolean>(false)

    const formatNumber = (n: any) => n.toString().padStart(2, '0');

    const presetType = requirements?.preset?.find((e) => e.key === config?.current_modal_type)?.presetType
    const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState<string | undefined>(undefined)

    const presetItemsOfInterest = presetInfo?.items?.filter((e) => e.type === presetType)

    const configSelections = (config as any)[`${metricSelectorPrefix}-metrics`]
    const [selections, setSelections] = useState<{ 'metric'?: string, 'agg': string }[]>(
        configSelections ||
        getEmptySelections(requirements?.metric_selector_info?.count || 2)
    );

    const getPresetSelected = () => presetItemsOfInterest?.find((preset) => preset.name === currentSelectedTilePreset)
    const presetItems = presetItemsOfInterest?.map((preset) => {
        return {
            label: preset.name,
            value: preset.name,
            // Any staff user can use a preset. Only its creator can save over it or delete it.
            disabled: false,
            deletable: preset.creator_id__email === user?.email,
        }
    })

    useEffect(() => {
        const presetSelected = getPresetSelected();
        if (presetSelected?.config) {
            const presetConfig = JSON.parse(JSON.stringify(presetSelected.config));
            setSelections(presetConfig);
            setResult && setResult(presetConfig);
        } else if (configSelections?.length > 0) {
            setSelections(JSON.parse(JSON.stringify(configSelections)));
        } else {
            setSelections(getEmptySelections(requirements?.metric_selector_info?.count || 2));
        }
    }, [currentSelectedTilePreset]);

    const addPreset = async (item: string) => {
        if (!presetType) {
            audioMessage.error('Unable to determine preset type. Please try again.');
            return;
        }
        try {
            // createPreset returns the full, updated presets list for the team
            const updatedPresets = await trainingAPI.createPreset({ name: item, type: presetType, config: selections });
            dispatch(setTeamPresets(updatedPresets));
            setCurrentSelectedTilePreset(item);
            setResult && setResult(selections);
            audioMessage.success('Preset saved successfully!');
        } catch (error) {
            audioMessage.error('Failed to save preset. Please try again.');
        }
    }

    const deletePreset = async (item: string) => {
        const presetOfInterest = presetItemsOfInterest?.find((preset) => preset.name === item)
        await trainingAPI.deletePreset(presetOfInterest?.preset_id)

        const updatedPreset = presetInfo?.items.filter(preset => preset.preset_id !== presetOfInterest?.preset_id)
        dispatch(setTeamPresets(updatedPreset))

        setCurrentSelectedTilePreset((presetItems && presetItems?.length > 0) ? presetItems[0].value : undefined)
    }

    const updatedPreset = async () => {
        const presetModified = presetItemsOfInterest?.find((preset) => preset.name === currentSelectedTilePreset)
        await trainingAPI.updatePreset({
            name: currentSelectedTilePreset, type: presetType, config: selections, preset_id: presetModified?.preset_id
        });
        const updatedItems = (presetInfo?.items || []).map((preset: any) =>
            preset && preset.preset_id === presetModified?.preset_id ? { ...preset, config: selections } : preset
        );
        dispatch(setTeamPresets(updatedItems));

        setUnsavedPresetChanges(false)
        message.success("Saved!");
    }

    const getTooltipText = () => {
        if (getPresetSelected()?.creator_id__email !== user?.email) {
            return 'Cannot modify tiles created by another user'
        }

        if (unsavedPresetChanges) {
            return 'You have unsaved changes'
        }

        return undefined
    }

    useEffect(() => {
        if (presetItems && presetItems.length === 0) {
            setCurrentSelectedTilePreset(undefined)
        }
    }, [presetItems])

    const metricDropdown = useMemo(() => {
        let optGroupDropdown: any[] = [];

        const { filteredMetricTypes, gpsLabelMap, testingLabelMap, forceplateLabelMap } =
            metricsSelectDropdown(
                all_metric_types,
                gpsDropdowns,
                forceplateDropdowns,
                testingDropdowns
            );

        const showAllMetrics = requirements?.metric_selector_info?.showAllMetrics || false;

        requirements?.metric_selector_info?.types?.forEach((metric_type: any) => {
            const categoryLabel =
                metric_type === "gps"
                    ? "GPS"
                    : metric_type.charAt(0).toUpperCase() + metric_type.slice(1).toLowerCase();

            if (categoryLabel === "Wellness") return;

            const sourceMetrics = showAllMetrics
                ? (all_metric_types?.[metric_type] || []).filter(
                      (metric: any) => metric.visible === true || metric.visible === undefined
                  )
                : (filteredMetricTypes?.[metric_type] || []);

            const groupOptions =
                sourceMetrics.map((metric: any) => {
                    const { unique_identifier, name } = metric;

                    // showAllMetrics (Percent of Best): show raw metric names.
                    // The curated label maps alias the Left member's key as
                    // "[Asym] X", which misrepresents what the report renders.
                    let displayLabel = name;
                    if (!showAllMetrics) {
                        if (metric_type === "gps" && gpsLabelMap.has(unique_identifier)) {
                            displayLabel = gpsLabelMap.get(unique_identifier);
                        } else if (
                            metric_type === "forceplate" &&
                            forceplateLabelMap.has(unique_identifier)
                        ) {
                            displayLabel = forceplateLabelMap.get(unique_identifier);
                        } else if (
                            metric_type === "testing" &&
                            testingLabelMap.has(unique_identifier)
                        ) {
                            displayLabel = testingLabelMap.get(unique_identifier);
                        }
                    }

                    return {
                        label: name,
                        displayLabel,
                        value: unique_identifier,
                        key: unique_identifier,
                    };
                });

            if (categoryLabel !== "GPS") {
                groupOptions.sort((a, b) =>
                    a.label.toLowerCase().localeCompare(b.label.toLowerCase())
                );
            }

            if (groupOptions.length) {
                optGroupDropdown.push({
                    label: categoryLabel,
                    displayLabel: categoryLabel,
                    key: metric_type,
                    options: groupOptions,
                });
            }
        });

        return optGroupDropdown;
    }, [
        all_metric_types,
        gpsDropdowns,
        forceplateDropdowns,
        testingDropdowns,
        requirements?.metric_selector_info?.types,
        requirements?.metric_selector_info?.showAllMetrics,
    ]);


    // Partial selections are pushed straight into the report config so that
    // closing the modal without saving still reflects what the user picked —
    // the selection count then shows e.g. 18/20 instead of silently resetting.
    const commitSelections = (next: { 'metric'?: string, 'agg': string }[]) => {
        setSelections(next);
        setResult && setResult(next);
    };

    const handleMetricChange = (index: any, metric: any) => {
        commitSelections(selections.map((row, i) => (i === index ? { ...row, metric } : row)));
    };

    const handleAggChange = (index: any, agg: any) => {
        commitSelections(selections.map((row, i) => (i === index ? { ...row, agg } : row)));
    };

    const handleSubmit = () => {

        // require all slots to be filled
        for (let i = 0; i < selections.length; i++) {
            if (!selections[i].metric) {
                audioMessage.error(`Please select a metric for Metric #${formatNumber(i + 1)}`);
                return;
            }
        }

        // Check for duplicates
        if (requirements?.metric_selector_info?.unique) {
            const metricValues = selections.map((e: any) => e.metric);
            if (metricValues.length !== (new Set(metricValues)).size) {
                audioMessage.error(`Please select metrics without any duplicates`);
                return;
            }
        }

        setResult && setResult(selections);
        setOpen && setOpen(false);
    };

    const reportConfig = requirements;
    if (!reportConfig) return <p>Invalid report type provided.</p>;

    const get_helper_text = () => {
        if (requirements?.metric_selector_info?.show_helper_text) {
            let i = 1;
            return (
                <Text type="secondary" style={{ marginBottom: "10px" }}>
                    Based on your data stream selections, you need to select {requirements?.metric_selector_info?.count} metrics. <br />
                    <ul>
                        {requirements?.metric_selector_info?.types?.map((type: string) => {
                            let line;
                            if (type === "report-card") {
                                line = <li>One Pager Report Card (20): Metric #{i} - Metric #{i + 19}</li>
                                i += 20;
                            } else if (type === "gps") {
                                line = <li>GPS (4): Metric #{i} - Metric #{i + 3}</li>
                                i += 4;
                            } else if (type === "forceplate") {
                                line = <li>Force Plate (4): Metric #{i} - Metric #{i + 3}</li>
                                i += 4;
                            } else if (type === "testing") {
                                line = <li>Testing(4): Metric #{i} - Metric #{i + 3}</li>
                                i += 4;
                            } else {
                                return line
                            }
                            return line;
                        })}
                    </ul>
                </Text>
            );
        } else {
            return null;
        }

    }

    return (
        <div>
            {get_helper_text()}
            {(presetInfo && presetType) &&
                <>
                    <div style={{ width: '700px', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <div style={{ flexGrow: 1 }}>
                            <SelectWithAdd
                                items={presetItems || []}
                                addItem={addPreset}
                                deleteItem={deletePreset}
                                placeholder="Select Preset"
                                setValue={setCurrentSelectedTilePreset}
                                currentSelectedTilePreset={currentSelectedTilePreset}
                                addItemLabel={"Add"}
                                addItemTextboxLabel={"Enter Preset Name"}
                                label={"Would you like to select pre-configured metrics?"}
                            />
                        </div>

                        <div>
                            <Tooltip title={getTooltipText() && getTooltipText()}>
                                <Button type="primary" size="small"
                                    onClick={updatedPreset}
                                    disabled={getPresetSelected()?.creator_id__email !== user?.email}
                                    style={{ width: "100px" }}
                                    className={(getPresetSelected()?.creator_id__email === user?.email && unsavedPresetChanges) ? 'pulse' : ''}
                                >
                                    Save Preset
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                </>
            }
            {selections.map((row: any, idx: number) => {
                const metricLabel = `Metric #${formatNumber(idx + 1)}`;
                return (
                <Row key={`${row['metric']}-${idx}`} align="middle">
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                        <Text strong style={{ marginRight: '10px' }}>{metricLabel}: </Text>
                        <Select
                            options={metricDropdown}
                            placeholder="Choose Metric"
                            style={{ width: 400, marginRight: "10px" }}
                            onChange={(value) => {
                                setUnsavedPresetChanges(true)
                                handleMetricChange(idx, value)
                            }}
                            value={row['metric']}
                            showSearch
                            fieldNames={{ label: 'displayLabel', value: 'value' }}
                            filterOption={(input, option) => {
                                return option?.displayLabel?.toLowerCase()?.includes(input.toLowerCase()) || false;
                            }
                            }
                        />
                        {
                            showAggs ? (
                                <Segmented
                                    options={[
                                        { label: 'Avg', value: 'avg' },
                                        { label: 'Best', value: 'best' },
                                        { label: 'Sum', value: 'sum' }]}
                                    onChange={(value) => {
                                        setUnsavedPresetChanges(true)
                                        handleAggChange(idx, value);
                                    }}
                                    value={row['agg']}
                                />) : null
                        }
                    </div>
                </Row>
            );
            })}

            <Button
                type="primary"
                style={{ width: "100%" }}
                onClick={() => handleSubmit()}
            >
                Save Configuration
            </Button>
        </div>
    );
};